import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { getCurrentSession, signOut as apiSignOut, getUserProfile } from './api';
import * as Sentry from '@sentry/react';

export type UserRole = 'site_engineer' | 'manager';

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active?: boolean;
}

interface UserContextType {
  currentUser: User;
  setCurrentUser: (user: User) => void;
  signOut: () => Promise<void>;
  loading: boolean;
  initialized: boolean;
}

const nullUser: User = {
  id: '',
  email: '',
  full_name: '',
  role: 'site_engineer',
  is_active: true,
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User>(nullUser);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const isMounted = useRef(true);
  const currentUserRef = useRef(currentUser);

  // Keep ref in sync with current user to avoid stale closures
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    isMounted.current = true;

    const processSession = async () => {
      try {
        const session = await getCurrentSession();
        if (!session?.user) {
          if (isMounted.current) {
            setCurrentUser(nullUser);
            setLoading(false);
            setInitialized(true);
          }
          return;
        }

        // Retry logic: session might take a moment to establish for RLS
        let profile = null;
        let retries = 5;

        while (retries > 0 && !profile) {
          profile = await getUserProfile(session.user.id);
          if (!profile) {
            retries--;
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
        }

        if (!isMounted.current) return;

        if (profile) {
          // Check if user is active
          if (profile.is_active === false) {
            await apiSignOut();
            setCurrentUser(nullUser);
            setLoading(false);
            setInitialized(true);
            return;
          }
          setCurrentUser(profile);
        } else {
          // No profile found - reject the user
          console.error('[Auth] No profile found for user:', session.user.id);
          await apiSignOut();
          setCurrentUser(nullUser);
          setLoading(false);
          setInitialized(true);
          return;
        }
      } catch (err) {
        console.error('Error handling auth state change:', err);
        Sentry.captureException(err, { extra: { context: 'Handling auth state change in userContext' } });
      } finally {
        if (isMounted.current) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    processSession();

    // Handle visibility change - refresh session when tab becomes active
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && currentUserRef.current.id) {
        try {
          // Verify session is still valid
          const session = await getCurrentSession();
          if (!session?.user) {
            console.log('[Auth] Session expired while inactive');
            await apiSignOut();
            setCurrentUser(nullUser);
            setLoading(false);
            setInitialized(true);
          }
        } catch (err) {
          console.error('[Auth] Error checking session on visibility change:', err);
          Sentry.captureException(err, { extra: { context: 'Checking session on visibility change in userContext' } });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await apiSignOut();
      setCurrentUser(nullUser);
    } catch (err) {
      console.error('Sign out error:', err);
      Sentry.captureException(err, { extra: { context: 'Signing out in userContext' } });
    }
  };

  const value = {
    currentUser,
    setCurrentUser,
    signOut: handleSignOut,
    loading,
    initialized,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser(): UserContextType {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export function useIsAuthenticated(): boolean {
  const { currentUser, initialized } = useUser();
  return initialized && !!currentUser.id && !!currentUser.email;
}
